import React, { useCallback, useMemo, useState } from 'react';
import { Formik, Field, FormikHelpers, FieldProps, ErrorMessage, Form } from 'formik';
import { ClientMonitoredTiktokAudioRelationship, patchClientMonitoredTiktokAudioRelationship } from '@round/api';
import Modal, { ModalContent, ModalFooter, ModalTitle } from '../../../../../../../ui/Layout/Modals/Modal/Modal';
import Button from '../../../../../../../ui/Buttons/Button/Button';
import Select from '../../../../../../../ui/DataEntry/Select/Select';
import Label from '../../../../../../../ui/DataEntry/Label/Label';
import useNonNullContext from '../../../../../../../Hooks/useNonNullContext';
import { OptionsContext } from '../../../../../../../contexts/Options';
import { GenericDropdownOption } from '../../../../../../../App.types';
import FormField from '../../../../../../../ui/DataEntry/FormField/FormField';
import { StylesConfig, ValueType } from 'react-select';
import { showNotification } from '../../../../../../../helpers';
import * as yup from 'yup';
import styles from './AddCustomName.module.css';
import { ReactComponent as AddIcon } from '../../../../../../../assets/Add.svg';

type AddCustomNameProps = {
    clientMonitoredTiktokAudioRelations: ClientMonitoredTiktokAudioRelationship[];
    onCustomNameAdded?: (relation: ClientMonitoredTiktokAudioRelationship) => void;
};

type FormValues = {
    client: ValueType<GenericDropdownOption<number>, false>;
    customName: string;
};

const initialValues: FormValues = {
    client: null,
    customName: '',
};

const validationSchema = yup.object({
    client: yup.object().nullable().required().label('client'),
    customName: yup.string().required().label('custom name'),
});

const clientSelectStyles: StylesConfig = {
    valueContainer: (base) => ({
        ...base,
        padding: '0.75rem 1rem',
    }),
    control: (base) => ({
        ...base,
        borderColor: '#c2cfe0',
        fontWeight: 400,
        fontSize: '0.8125rem',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        borderColor: '#c2cfe0',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#90a0b7',
        fontWeight: 400,
        fontSize: '0.8125rem',
    }),
};

const AddCustomName = ({ clientMonitoredTiktokAudioRelations, onCustomNameAdded }: AddCustomNameProps) => {
    const { clients } = useNonNullContext(OptionsContext);
    const [customNameModalOpen, setCustomNameModalOpen] = useState(false);

    const relationsWithoutCustomName = useMemo(
        () => clientMonitoredTiktokAudioRelations.filter((rel) => !Boolean(rel.custom_name.length)),
        [clientMonitoredTiktokAudioRelations]
    );

    const clientOptions: GenericDropdownOption<number>[] = useMemo(() => {
        const audioClientsWithoutCustomName = relationsWithoutCustomName.map((rel) => rel.client_id);
        return clients
            .filter((c) => audioClientsWithoutCustomName.includes(c.id))
            .map((c) => ({ value: c.id, label: c.name }));
    }, [clients, relationsWithoutCustomName]);

    const handleSubmit = useCallback(
        async (values: FormValues, { setSubmitting, resetForm, setErrors }: FormikHelpers<FormValues>) => {
            try {
                setSubmitting(true);
                const relation = relationsWithoutCustomName.find((rel) => rel.client_id === values.client?.value);
                if (!relation) {
                    setErrors({ client: 'Field is required' });
                    return;
                }
                const response = await patchClientMonitoredTiktokAudioRelationship(relation.id, {
                    custom_name: values.customName,
                });

                if (response.status !== 200) {
                    throw new Error('Could not add custom name');
                }

                if (typeof onCustomNameAdded === 'function') {
                    onCustomNameAdded(response.data);
                }

                resetForm();
                setCustomNameModalOpen(false);
                showNotification('Custom name set', 'info');
            } catch {
                showNotification('Could not add custom name', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        [onCustomNameAdded, relationsWithoutCustomName]
    );

    return (
        <>
            <Button
                type="link"
                className={styles.addCustomNameButton}
                onClick={() => setCustomNameModalOpen(true)}
                hint="Add custom name"
            >
                <AddIcon className={styles.addIcon} />
            </Button>

            <Modal closeOnOverlayClick isOpen={customNameModalOpen} onClose={() => setCustomNameModalOpen(false)}>
                <ModalTitle>Add custom name</ModalTitle>
                <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
                    {({ dirty, isSubmitting, isValid }) => {
                        const submitDisabled = !dirty || !isValid;
                        return (
                            <>
                                <Form>
                                    <ModalContent>
                                        <FormField>
                                            <Label htmlFor="client">Client</Label>
                                            <Field name="client">
                                                {(props: FieldProps) => (
                                                    <Select
                                                        id="client"
                                                        placeholder="Select client"
                                                        styles={clientSelectStyles}
                                                        options={clientOptions}
                                                        value={props.field.value}
                                                        onChange={(value) =>
                                                            props.form.setFieldValue(props.field.name, value)
                                                        }
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="client">
                                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                            </ErrorMessage>
                                        </FormField>

                                        <FormField>
                                            <Label htmlFor="customName">Custom name</Label>
                                            <Field id="customName" name="customName" placeholder="Enter custom name" />
                                            <ErrorMessage name="customName">
                                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                            </ErrorMessage>
                                        </FormField>
                                    </ModalContent>
                                    <ModalFooter>
                                        <Button
                                            type="filled"
                                            color="black"
                                            htmlType="submit"
                                            disabled={submitDisabled}
                                            loading={isSubmitting}
                                        >
                                            Add
                                        </Button>
                                    </ModalFooter>
                                </Form>
                            </>
                        );
                    }}
                </Formik>
            </Modal>
        </>
    );
};

export default AddCustomName;
